import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import API, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Plus, Wallet, TrendingDown, CheckCircle, XCircle } from "lucide-react";

export default function AdvancesPage() {
  const { user } = useAuth();
  const [advances, setAdvances] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({
    employee_id: "",
    amount: "",
    description: "",
    repayment_months: "1",
  });

  const isAdmin = user?.role === "admin";

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      const [advRes, empRes] = await Promise.all([
        API.get("/advances"),
        API.get("/employees"),
      ]);
      setAdvances(advRes.data);
      setEmployees(empRes.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }

  async function handleCreate(e) {
    e.preventDefault();
    try {
      await API.post("/advances", {
        employee_id: form.employee_id,
        amount: parseFloat(form.amount),
        description: form.description,
        repayment_months: parseInt(form.repayment_months),
      });
      toast.success("Advance assigned successfully");
      setDialogOpen(false);
      setForm({ employee_id: "", amount: "", description: "", repayment_months: "1" });
      loadData();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  }

  async function handleStatusChange(advanceId, status) {
    try {
      await API.put(`/advances/${advanceId}`, { status });
      toast.success(`Advance ${status === "cancelled" ? "cancelled" : "marked as fully repaid"}`);
      loadData();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  }

  function getStatusBadge(status) {
    const variants = {
      active: "bg-blue-100 text-blue-800",
      fully_repaid: "bg-green-100 text-green-800",
      cancelled: "bg-red-100 text-red-800",
    };
    return (
      <Badge className={variants[status] || "bg-gray-100 text-gray-800"}>
        {status?.replace("_", " ")}
      </Badge>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-sm text-slate-500">Loading advances...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Advances</h1>
          <p className="text-sm text-slate-500 mt-1">
            Manage salary advances and deductions
          </p>
        </div>
        {isAdmin && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#002FA7] hover:bg-[#002FA7]/90">
                <Plus className="h-4 w-4 mr-2" />
                Assign Advance
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Assign Salary Advance</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4">
                <div>
                  <Label>Employee</Label>
                  <select
                    className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md text-sm"
                    value={form.employee_id}
                    onChange={(e) => setForm({ ...form, employee_id: e.target.value })}
                    required
                  >
                    <option value="">Select employee...</option>
                    {employees.map((emp) => (
                      <option key={emp.id} value={emp.id}>
                        {emp.name} - {emp.department}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Amount (KES)</Label>
                  <Input
                    type="number"
                    min="1"
                    step="0.01"
                    placeholder="e.g. 50000"
                    value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label>Repayment Period (Months)</Label>
                  <Input
                    type="number"
                    min="1"
                    placeholder="e.g. 6"
                    value={form.repayment_months}
                    onChange={(e) => setForm({ ...form, repayment_months: e.target.value })}
                    required
                  />
                  {form.amount && form.repayment_months && (
                    <p className="text-xs text-slate-500 mt-1">
                      Monthly deduction: KES {(parseFloat(form.amount || 0) / parseInt(form.repayment_months || 1)).toFixed(2)}
                    </p>
                  )}
                </div>
                <div>
                  <Label>Description (Optional)</Label>
                  <Input
                    placeholder="Reason for advance"
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                  />
                </div>
                <div className="flex justify-end gap-3 pt-2">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-[#002FA7] hover:bg-[#002FA7]/90">
                    Assign Advance
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-500">Total Advances</CardTitle>
            <Wallet className="h-4 w-4 text-slate-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{advances.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-500">Active Advances</CardTitle>
            <TrendingDown className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              {advances.filter((a) => a.status === "active").length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-500">Fully Repaid</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {advances.filter((a) => a.status === "fully_repaid").length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Advances Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Employee</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Monthly Deduction</TableHead>
                <TableHead>Remaining Balance</TableHead>
                <TableHead>Repayment</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Date</TableHead>
                {isAdmin && <TableHead className="text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {advances.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isAdmin ? 8 : 7} className="text-center text-slate-500 py-8">
                    No advances found
                  </TableCell>
                </TableRow>
              ) : (
                advances.map((adv) => (
                  <TableRow key={adv.id}>
                    <TableCell>
                      <div className="font-medium">{adv.employee_name}</div>
                      <div className="text-xs text-slate-500">{adv.employee_email}</div>
                    </TableCell>
                    <TableCell className="font-medium">KES {adv.amount?.toLocaleString()}</TableCell>
                    <TableCell>KES {adv.monthly_deduction?.toLocaleString()}</TableCell>
                    <TableCell>KES {adv.remaining_balance?.toLocaleString()}</TableCell>
                    <TableCell className="text-sm">
                      {adv.repayment_months} month{adv.repayment_months > 1 ? "s" : ""}
                    </TableCell>
                    <TableCell>{getStatusBadge(adv.status)}</TableCell>
                    <TableCell className="text-sm text-slate-500">
                      {new Date(adv.created_at).toLocaleDateString()}
                    </TableCell>
                    {isAdmin && (
                      <TableCell className="text-right">
                        {adv.status === "active" && (
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-green-600 hover:text-green-700"
                              onClick={() => handleStatusChange(adv.id, "fully_repaid")}
                              title="Mark as fully repaid"
                            >
                              <CheckCircle className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-700"
                              onClick={() => handleStatusChange(adv.id, "cancelled")}
                              title="Cancel advance"
                            >
                              <XCircle className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
